import { clampMetaTitle } from './clamp-meta-title';
import type { DimensionConfig } from '../../pages/best-companies/dimensionConfig';
import { getBestCompaniesDimensionDisplayLabel } from './best-companies-dimension-labels';
import { localizeRoute } from '../constants/url-constants';

export const BEST_COMPANIES_SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;
export type BestCompaniesLocale = (typeof BEST_COMPANIES_SUPPORTED_LOCALES)[number];

export function isBestCompaniesLocale(value: string): value is BestCompaniesLocale {
  return (BEST_COMPANIES_SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function normalizeBestCompaniesLocale(value: string | undefined): BestCompaniesLocale {
  if (value && isBestCompaniesLocale(value)) return value;
  return 'en';
}

export function inferBestCompaniesLocaleFromPathname(pathname: string): BestCompaniesLocale {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];
  return isBestCompaniesLocale(first) ? first : 'en';
}

/** Calendar year for rankings copy (title, H1, freshness). */
export function getBestCompaniesHubRankingYear(referenceDate: Date = new Date()): number {
  return referenceDate.getFullYear();
}

/**
 * Clamp meta description (~160 chars) at a word boundary when possible.
 */
export function clampMetaDescription(text: string, maxLength = 160): string {
  const t = text.trim();
  if (t.length <= maxLength) return t;
  const cut = t.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > Math.min(100, maxLength - 20)) {
    return `${cut.slice(0, lastSpace).trim()}…`;
  }
  return `${cut.trim()}…`;
}

/** English logical path (Vike `urlLogical`). */
export function bestCompaniesPath(dimensionSlug?: string): string {
  return dimensionSlug ? `/best-companies/${dimensionSlug}` : '/best-companies';
}

/**
 * Public URL path including locale prefix and **localized** hub segment
 * (EN `best-companies`, FI `parhaat-yritykset`, SV `basta-foretag`) — use for canonical, hreflang, sitemap.
 */
export function bestCompaniesCanonicalPath(locale: BestCompaniesLocale, dimensionSlug?: string): string {
  const logical = bestCompaniesPath(dimensionSlug);
  return `/${locale}${localizeRoute(logical, locale)}`;
}

export function buildBestCompaniesHubSeo(
  locale: BestCompaniesLocale,
  options?: { year?: number },
): {
  title: string;
  description: string;
  keywords: string;
  ogLocale: string;
} {
  const year = options?.year ?? getBestCompaniesHubRankingYear();
  if (locale === 'fi') {
    return {
      title: clampMetaTitle(`Parhaat yritykset työpaikkana Suomessa ${year} | JobCrawls`),
      description: clampMetaDescription(
        `${year}: parhaat työnantajat ja yritykset töihin Suomeen — sijoitus live-työpaikkailmoituksista (myös etä- ja hybridityö, Helsinki mukaan). JobCrawls Employer Score 0–100: palkka‑avoimuus, joustavuus, rekrytointi. Ei maksettuja arvosteluja.`,
      ),
      keywords:
        `parhaat työnantajat Suomi ${year}, parhaat yritykset töihin Suomeen, huipputyönantajat Helsinki, etätyö Suomi, työnantajien sijoitukset, JobCrawls Employer Score`,
      ogLocale: 'fi_FI',
    };
  }
  if (locale === 'sv') {
    return {
      title: clampMetaTitle(`Bästa företagen att jobba på i Finland ${year} | JobCrawls`),
      description: clampMetaDescription(
        `${year}: topparbetsgivare i Finland från riktiga platsannonser (distans och hybrid, inkl. Helsingfors). JobCrawls Employer Score 0–100: lönetransparens, flexibilitet, rekrytering. Inte betalda omdömen.`,
      ),
      keywords:
        `bästa arbetsgivare Finland ${year}, bästa företag att jobba på Finland, topparbetsgivare Helsingfors, distansjobb Finland, JobCrawls Employer Score`,
      ogLocale: 'sv_SE',
    };
  }
  return {
    title: clampMetaTitle(`Best Companies to Work For in Finland ${year} | JobCrawls`),
    description: clampMetaDescription(
      `${year} rankings: best companies to work for and top companies in Finland from live job listings (remote and hybrid roles, including Helsinki). JobCrawls Employer Score is 0–100 from listing signals—salary transparency, flexibility, hiring—not paid reviews.`,
    ),
    keywords:
      `best companies to work for in Finland, top companies Finland ${year}, best companies Finland ${year}, best places to work Helsinki, remote jobs Finland, JobCrawls employer score, salary transparency`,
    ogLocale: 'en_US',
  };
}

export function buildBestCompaniesDimensionSeo(
  locale: BestCompaniesLocale,
  config: DimensionConfig,
  dimensionSlug: string
): { title: string; description: string; keywords: string; ogLocale: string } {
  const ogLocale = locale === 'fi' ? 'fi_FI' : locale === 'sv' ? 'sv_SE' : 'en_US';
  const typeKey = config.type === 'city' ? 'city' : 'industry';
  const displayLabel =
    getBestCompaniesDimensionDisplayLabel(dimensionSlug, locale) ?? config.label;

  if (locale === 'fi') {
    const title = clampMetaTitle(`Parhaat työnantajat: ${displayLabel} | JobCrawls`);
    const description = clampMetaDescription(
      `Parhaat työnantajat segmentissä ${displayLabel} työpaikoilla, jotka kohdistuvat Suomen työmarkkinalle (mukaan lukien etätyö). JobCrawls Employer Score — ei käyttäjäarvosteluja.`
    );
    const keywords = `parhaat työnantajat, ${displayLabel}, Suomi, työnantajapisteet, ${typeKey === 'city' ? 'kaupunki' : 'toimiala'}, JobCrawls`;
    return { title, description, keywords, ogLocale };
  }
  if (locale === 'sv') {
    const title = clampMetaTitle(`Bästa arbetsgivare: ${displayLabel} | JobCrawls`);
    const description = clampMetaDescription(
      `Topp arbetsgivare inom ${displayLabel} bland annonser riktade till den finska arbetsmarknaden (distans inräknat). JobCrawls Employer Score — inte betalda omdömen.`
    );
    const keywords = `bästa arbetsgivare, ${displayLabel}, Finland, arbetsgivarpoäng, ${typeKey === 'city' ? 'stad' : 'bransch'}, JobCrawls`;
    return { title, description, keywords, ogLocale };
  }

  const title = clampMetaTitle(`Best Employers in ${config.metaTitleSuffix} | JobCrawls`);
  const description = clampMetaDescription(
    `Top companies in ${displayLabel} among listings aimed at the Finnish job market (including remote). JobCrawls Employer Score: salary transparency, flexibility, hiring data. Not reviews.`
  );
  const keywords = `best companies, ${displayLabel}, Finland, employer rankings, ${config.type}, JobCrawls employer score`;
  return { title, description, keywords, ogLocale };
}
